import { Autocomplete, Box, Chip } from "@mui/material";
import { type FC, useMemo, useState } from "react";
import CommonTextField from "../../commonTextField/CommonTextField";
import InputHelperText from "../../error/InputHelperText";
import { RoundedButton } from "../../../styled/styled";
import { useTranslation } from "react-i18next";

export type SearchTermsFieldProps = {
  value: string[];
  onChange: (searchTerms: string[]) => void;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  placeholder?: string;
  helperText?: string;
  addLabel?: string;
  showAddButton?: boolean;
  disableClearable?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
};

const SearchTermsField: FC<SearchTermsFieldProps> = ({
  value,
  onChange,
  inputValue,
  onInputChange,
  placeholder,
  helperText,
  addLabel,
  showAddButton = true,
  disableClearable = true,
  fullWidth = true,
  size = "small",
}) => {
  const [internalInputValue, setInternalInputValue] = useState<string>("");

  const { t } = useTranslation();

  const actualInputValue = useMemo(
    () => (inputValue !== undefined ? inputValue : internalInputValue),
    [inputValue, internalInputValue],
  );

  const setInputValue = (nextValue: string) => {
    onInputChange?.(nextValue);
    if (inputValue === undefined) {
      setInternalInputValue(nextValue);
    }
  };

  const addTerm = (rawTerm?: string) => {
    const term = (rawTerm ?? actualInputValue).trim();
    if (!term) return;

    onChange([...value, term]);
    setInputValue("");
  };

  return (
    <Box position="relative" width={fullWidth ? "100%" : undefined}>
      <Box display="flex" alignItems="center" gap="8px" flexWrap="nowrap">
        <Box flex={1} minWidth={0}>
          <Autocomplete
            fullWidth={fullWidth}
            multiple
            freeSolo
            options={[]}
            disableClearable={disableClearable}
            value={value}
            inputValue={actualInputValue}
            onInputChange={(_, newInputValue) => {
              setInputValue(newInputValue);
            }}
            onChange={(_, newValue) => {
              onChange(newValue as string[]);
            }}
            size={size}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            sx={{
              "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
                {
                  minWidth: "30px",
                },
            }}
            renderInput={(params) => (
              <CommonTextField
                {...params}
                placeholder={placeholder || t("common:addSearchTerm")}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;

                  // Prevent MUI from handling Enter
                  e.preventDefault();
                  e.stopPropagation();

                  addTerm();
                }}
              />
            )}
          />
        </Box>
        {showAddButton ? (
          <RoundedButton
            size="small"
            variant="contained"
            onClick={() => addTerm()}
            disabled={!actualInputValue.trim()}
          >
            {addLabel || t("common:add")}
          </RoundedButton>
        ) : null}
      </Box>
      {helperText ? (
        <Box position="absolute" left={0} top="100%" mt="4px" width="100%">
          <InputHelperText helperText={helperText} />
        </Box>
      ) : null}
    </Box>
  );
};

export default SearchTermsField;
